import { Button, Result, Spin } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileTypeEnum } from '../../../enums/FileTypeEnum';
import type FileInterface from '../../../interfaces/File';
import type ViewerFile from '../../../interfaces/ViewerFile';
import ModalLayout from '../../../layouts/ModalLayout';
import { FileService } from '../../../services/fileApiService';
import { FunctionService } from '../../../services/functionService';
import { decryptBlobAsText } from '../../../utils/blobDecryption';
import FileViewerSecret from '../ViewerFileSecret';

interface Props {
  file: FileInterface;
  /** Destroy hosting modal (Cancel / after Save). */
  onClose?: () => void;
}

const fileService = new FileService();
const functionService = new FunctionService();

/**
 * FileSecretDetail loads and decrypts a .fgsecret for the open-in-modal path.
 * Loading/error use ModalLayout; loaded content delegates ModalLayout + edit UX to ViewerFileSecret.
 */
export const FileSecretDetail = ({ file, onClose }: Props) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewerFile, setViewerFile] = useState<ViewerFile | null>(null);

  const isFetchedRef = useRef(false);

  const fetchFile = useCallback(async () => {
    if (isFetchedRef.current) return;
    isFetchedRef.current = true;

    try {
      setIsLoading(true);

      const fileWithToken = await fileService.getFileForViewer(file.id, { type: 'text' });
      const blob = await functionService.getFileBlob(fileWithToken.token, fileWithToken.fileSize);
      const { decrypted, encryptedBytes } = await decryptBlobAsText(blob);

      const vf: ViewerFile = {
        id: file.id,
        cufi: file.cufi,
        fileName: file.fileName,
        fileExtension: file.fileExtension,
        fileType: FileTypeEnum.Secret,
        expirationAt: fileWithToken.expirationAt || 0,
        token: fileWithToken.token,
        fileSize: fileWithToken.fileSize,
        isReadOnly: fileWithToken.isReadOnly || false,
        canDownload: fileWithToken.canDownload || false,
        fileLimitations: file.fileLimitations || [],
        decryptedFileBlob: decrypted,
        encryptedFileBytes: encryptedBytes,
      };

      setViewerFile(vf);
    } catch {
      setError(t('secret.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [file, t]);

  useEffect(() => {
    fetchFile();
  }, [fetchFile]);

  if (isLoading) {
    return (
      <ModalLayout>
        <ModalLayout.Content>
          <div className="flex h-48 items-center justify-center">
            <Spin size="large" />
          </div>
        </ModalLayout.Content>
      </ModalLayout>
    );
  }

  if (error || !viewerFile) {
    return (
      <ModalLayout>
        <ModalLayout.Content>
          <div className="flex h-48 items-center justify-center">
            <Result status="error" title={error || t('secret.loadError')} />
          </div>
        </ModalLayout.Content>
        {onClose && (
          <ModalLayout.Footer>
            <div className="flex flex-row items-center justify-end gap-2">
              <Button size="middle" onClick={onClose}>
                {t('common.cancel')}
              </Button>
            </div>
          </ModalLayout.Footer>
        )}
      </ModalLayout>
    );
  }

  // ViewerFileSecret (variant=modal) owns ModalLayout Content + Cancel/Save footer.
  return (
    <FileViewerSecret file={viewerFile} onLoaded={() => {}} variant="modal" onClose={onClose} />
  );
};

export default FileSecretDetail;
